import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { Plus, Pencil, Trash2, Pin, UserPlus, CalendarIcon, ChevronDown, Loader2, ImagePlus, X, FileText, ExternalLink, Upload } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TiptapEditor } from "@/components/ui/tiptap-editor";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsAdmin } from "@/hooks/use-admin";
import { useIsMobile } from "@/hooks/use-mobile";
import { api } from "@/convex";
import type { Id } from "../../convex/_generated/dataModel";
import { parseVimeoInput } from "@/lib/vimeo";
import { timezones, generateDateDisplay, generateTimeDisplay, generateMonthYearDisplay } from "@/lib/event-utils";
import { compressImage } from "@/lib/image-utils";

interface UpdateForm {
  title: string;
  author: string;
  date: string;
  category: string;
  excerpt: string;
  vimeoId: string;
  content: string;
}

interface MemberForm {
  name: string;
  company: string;
  title: string;
}

interface EventForm {
  title: string;
  date: string;
  endDate: string;
  dateTbd: boolean;
  hideTbdBadge: boolean;
  dateDisplay: string;
  time: string;
  startTime: string;
  endTime: string;
  timezone: string;
  location: string;
  type: string;
  status: string;
  pinned: boolean;
  imageStorageId: string; // Convex _storage id, "" when none
  imageUrl: string; // preview URL (existing image or freshly-uploaded object URL)
  zoomLink: string;
  meetingId: string;
  passcode: string;
  tierRequired: string;
}

const emptyUpdateForm: UpdateForm = {
  title: "",
  author: "Alex Epstein",
  date: "",
  category: "Policy Insider Call",
  excerpt: "",
  vimeoId: "",
  content: "",
};

const emptyMemberForm: MemberForm = {
  name: "",
  company: "",
  title: "",
};


const emptyEventForm: EventForm = {
  title: "",
  date: "",
  endDate: "",
  dateTbd: false,
  hideTbdBadge: false,
  dateDisplay: "",
  time: "",
  startTime: "",
  endTime: "",
  timezone: "America/Los_Angeles",
  location: "",
  type: "Member Call",
  status: "Confirmed",
  pinned: false,
  imageStorageId: "",
  imageUrl: "",
  zoomLink: "",
  meetingId: "",
  passcode: "",
  tierRequired: "",
};

const categories = [
  "Action Leaders Call",
  "Policy Insider Call",
  "AI Mastery Call",
  "Announcement",
  "Event",
];

const eventTypes = [
  "Action Leaders Call",
  "Member Call",
  "In-Person",
  "Annual Conference",
  "Webinar",
];

const eventStatuses = [
  "Confirmed",
  "RSVP Required",
  "Registration Open",
  "Tentative",
  "Cancelled",
];

const MAX_BROCHURE_BYTES = 25 * 1024 * 1024;

const formatFileSize = (bytes: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024)) + " MB";

const Admin = () => {
  const isAdmin = useIsAdmin();

  // Updates
  const updates = useQuery(api.updates.list, {});
  const createUpdate = useMutation(api.updates.create);
  const updateUpdate = useMutation(api.updates.update);
  const removeUpdate = useMutation(api.updates.remove);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<Id<"updates"> | null>(null);
  const [deleteId, setDeleteId] = useState<Id<"updates"> | null>(null);
  const [form, setForm] = useState<UpdateForm>(emptyUpdateForm);
  const [saving, setSaving] = useState(false);

  // Members
  const members = useQuery(api.members.list, {});
  const createMember = useMutation(api.members.create);
  const updateMember = useMutation(api.members.update);
  const removeMember = useMutation(api.members.remove);

  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<Id<"members"> | null>(null);
  const [deleteMemberId, setDeleteMemberId] = useState<Id<"members"> | null>(null);
  const [memberForm, setMemberForm] = useState<MemberForm>(emptyMemberForm);
  const [savingMember, setSavingMember] = useState(false);

  // Events
  const events = useQuery(api.events.listAll, {});
  const createEvent = useMutation(api.events.create);
  const updateEvent = useMutation(api.events.update);
  const removeEvent = useMutation(api.events.remove);
  const generateEventUploadUrl = useMutation(api.events.generateUploadUrl);
  const discardEventUpload = useMutation(api.events.discardUpload);

  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState<Id<"events"> | null>(null);
  const [deleteEventId, setDeleteEventId] = useState<Id<"events"> | null>(null);
  const [eventForm, setEventForm] = useState<EventForm>(emptyEventForm);
  const [savingEvent, setSavingEvent] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingEventImageStorageId, setPendingEventImageStorageId] = useState<Id<"_storage"> | null>(null);
  const eventImageInputRef = useRef<HTMLInputElement>(null);
  const eventImagePreviewUrlRef = useRef<string | null>(null);
  const [eventErrors, setEventErrors] = useState<Record<string, string>>({});

  // Mobile detection
  const isMobile = useIsMobile();

  // Update title ref
  const updateTitleRef = useRef<HTMLInputElement>(null);

  // Referrals
  const referrals = useQuery(api.referrals.list, isAdmin ? {} : "skip");
  const updateReferralStatus = useMutation(api.referrals.updateStatus);
  const removeReferral = useMutation(api.referrals.remove);

  const [deleteReferralId, setDeleteReferralId] = useState<Id<"referrals"> | null>(null);

  // Conference brochure
  const brochure = useQuery(api.conferenceBrochure.getAdmin, isAdmin ? {} : "skip");
  const generateBrochureUploadUrl = useMutation(api.conferenceBrochure.generateUploadUrl);
  const publishBrochure = useMutation(api.conferenceBrochure.publish);
  const discardBrochureUpload = useMutation(api.conferenceBrochure.discardUpload);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const brochureInputRef = useRef<HTMLInputElement>(null);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You do not have admin access.</p>
        </div>
      </div>
    );
  }

  // Updates handlers
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyUpdateForm);
    setDialogOpen(true);
  };

  const openEdit = (update: NonNullable<typeof updates>[number]) => {
    setEditingId(update._id);
    setForm({
      title: update.title,
      author: update.author,
      date: update.date,
      category: update.category,
      excerpt: update.excerpt,
      vimeoId: update.vimeoId || "",
      content: update.content || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date.trim() || !form.excerpt.trim()) return;
    setSaving(true);
    const parsedVimeoId = parseVimeoInput(form.vimeoId) || undefined;
    try {
      if (editingId) {
        await updateUpdate({
          id: editingId,
          title: form.title,
          author: form.author,
          date: form.date,
          category: form.category,
          excerpt: form.excerpt,
          vimeoId: parsedVimeoId,
          content: form.content || undefined,
        });
      } else {
        await createUpdate({
          title: form.title,
          author: form.author,
          date: form.date,
          category: form.category,
          excerpt: form.excerpt,
          vimeoId: parsedVimeoId,
          content: form.content || undefined,
        });
      }
      setDialogOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await removeUpdate({ id: deleteId });
    setDeleteId(null);
  };

  // Members handlers
  const openMemberCreate = () => {
    setEditingMemberId(null);
    setMemberForm(emptyMemberForm);
    setMemberDialogOpen(true);
  };

  const openMemberEdit = (member: NonNullable<typeof members>[number]) => {
    setEditingMemberId(member._id);
    setMemberForm({
      name: member.name,
      company: member.company,
      title: member.title,
    });
    setMemberDialogOpen(true);
  };

  const handleMemberSave = async () => {
    if (!memberForm.name.trim()) return;
    setSavingMember(true);
    try {
      if (editingMemberId) {
        await updateMember({
          id: editingMemberId,
          name: memberForm.name,
          company: memberForm.company,
          title: memberForm.title,
        });
      } else {
        await createMember({
          name: memberForm.name,
          company: memberForm.company,
          title: memberForm.title,
        });
      }
      setMemberDialogOpen(false);
    } finally {
      setSavingMember(false);
    }
  };

  const handleMemberDelete = async () => {
    if (!deleteMemberId) return;
    await removeMember({ id: deleteMemberId });
    setDeleteMemberId(null);
  };

  // Events handlers
  const openEventCreate = () => {
    setEditingEventId(null);
    setEventForm(emptyEventForm);
    setPendingEventImageStorageId(null);
    setEventErrors({});
    setEventDialogOpen(true);
  };

  const openEventEdit = (event: NonNullable<typeof events>[number]) => {
    setEditingEventId(event._id);
    setEventForm({
      title: event.title,
      date: event.date,
      endDate: event.endDate || "",
      dateTbd: event.dateTbd || false,
      hideTbdBadge: event.hideTbdBadge || false,
      dateDisplay: event.dateDisplay,
      time: event.time,
      startTime: event.startTime || "",
      endTime: event.endTime || "",
      timezone: event.timezone || "America/Los_Angeles",
      location: event.location,
      type: event.type,
      status: event.status,
      pinned: event.pinned,
      imageStorageId: event.imageStorageId || "",
      imageUrl: event.imageUrl || "",
      zoomLink: event.zoomLink || "",
      meetingId: event.meetingId || "",
      passcode: event.passcode || "",
      tierRequired: event.tierRequired || "",
    });
    setPendingEventImageStorageId(null);
    setEventErrors({});
    setEventDialogOpen(true);
  };

  const handleEventImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setUploadingImage(true);
    try {
      const blob = await compressImage(file);
      if (blob.size > 8 * 1024 * 1024) {
        throw new Error("Compressed image is larger than 8 MB");
      }
      const postUrl = await generateEventUploadUrl({});
      const res = await fetch(postUrl, {
        method: "POST",
        headers: { "Content-Type": blob.type || "image/jpeg" },
        body: blob,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const { storageId } = await res.json() as { storageId: Id<"_storage"> };
      const previousPendingId = pendingEventImageStorageId;
      const previewUrl = URL.createObjectURL(blob);
      if (eventImagePreviewUrlRef.current) URL.revokeObjectURL(eventImagePreviewUrlRef.current);
      eventImagePreviewUrlRef.current = previewUrl;
      setPendingEventImageStorageId(storageId);
      setEventForm((f) => ({ ...f, imageStorageId: storageId, imageUrl: previewUrl }));
      if (previousPendingId && previousPendingId !== storageId) {
        void discardEventUpload({ storageId: previousPendingId }).catch(console.error);
      }
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Image upload failed. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEventImageRemove = () => {
    if (pendingEventImageStorageId) {
      void discardEventUpload({ storageId: pendingEventImageStorageId }).catch(console.error);
      setPendingEventImageStorageId(null);
    }
    if (eventImagePreviewUrlRef.current) {
      URL.revokeObjectURL(eventImagePreviewUrlRef.current);
      eventImagePreviewUrlRef.current = null;
    }
    setEventForm((f) => ({ ...f, imageStorageId: "", imageUrl: "" }));
  };

  const handleEventDialogOpenChange = (open: boolean) => {
    if (!open && pendingEventImageStorageId) {
      void discardEventUpload({ storageId: pendingEventImageStorageId }).catch(console.error);
      setPendingEventImageStorageId(null);
    }
    if (!open && eventImagePreviewUrlRef.current) {
      URL.revokeObjectURL(eventImagePreviewUrlRef.current);
      eventImagePreviewUrlRef.current = null;
    }
    setEventDialogOpen(open);
  };

  const handleEventSave = async () => {
    const errors: Record<string, string> = {};
    if (!eventForm.title.trim()) errors.title = "Title is required";
    if (!eventForm.date.trim()) errors.date = "Date is required";
    if (Object.keys(errors).length > 0) {
      setEventErrors(errors);
      toast.error("Please fill in all required fields");
      return;
    }
    setEventErrors({});
    // Auto-generate display fields if empty. Month-only events (dateTbd) show
    // "August 2026" and carry no specific time / calendar-export data.
    const dateDisplay =
      eventForm.dateDisplay ||
      (eventForm.dateTbd
        ? generateMonthYearDisplay(eventForm.date)
        : generateDateDisplay(eventForm.date, eventForm.endDate));
    const timeDisplay = eventForm.dateTbd
      ? eventForm.time || "Date TBD"
      : eventForm.time || generateTimeDisplay(eventForm.startTime, eventForm.endTime, eventForm.timezone);
    if (!dateDisplay) {
      toast.error("Could not generate date display");
      return;
    }
    setSavingEvent(true);
    const calendarFields = eventForm.dateTbd
      ? { endDate: undefined, startTime: undefined, endTime: undefined, timezone: undefined }
      : {
          endDate: eventForm.endDate || undefined,
          startTime: eventForm.startTime || undefined,
          endTime: eventForm.endTime || undefined,
          timezone: eventForm.timezone || undefined,
        };
    try {
      if (editingEventId) {
        await updateEvent({
          id: editingEventId,
          title: eventForm.title,
          date: eventForm.date,
          dateTbd: eventForm.dateTbd,
          hideTbdBadge: eventForm.dateTbd ? eventForm.hideTbdBadge : false,
          ...calendarFields,
          dateDisplay,
          time: timeDisplay,
          location: eventForm.location,
          type: eventForm.type,
          status: eventForm.status,
          pinned: eventForm.pinned,
          imageStorageId: eventForm.imageStorageId ? (eventForm.imageStorageId as Id<"_storage">) : undefined,
          clearImage: eventForm.imageStorageId ? undefined : true,
          zoomLink: eventForm.zoomLink || undefined,
          meetingId: eventForm.meetingId || undefined,
          passcode: eventForm.passcode || undefined,
          tierRequired: eventForm.tierRequired || undefined,
        });
      } else {
        await createEvent({
          title: eventForm.title,
          date: eventForm.date,
          dateTbd: eventForm.dateTbd,
          hideTbdBadge: eventForm.dateTbd ? eventForm.hideTbdBadge : false,
          ...calendarFields,
          dateDisplay,
          time: timeDisplay,
          location: eventForm.location,
          type: eventForm.type,
          status: eventForm.status,
          pinned: eventForm.pinned,
          imageStorageId: eventForm.imageStorageId ? (eventForm.imageStorageId as Id<"_storage">) : undefined,
          zoomLink: eventForm.zoomLink || undefined,
          meetingId: eventForm.meetingId || undefined,
          passcode: eventForm.passcode || undefined,
          tierRequired: eventForm.tierRequired || undefined,
        });
      }
      setPendingEventImageStorageId(null);
      if (eventImagePreviewUrlRef.current) {
        URL.revokeObjectURL(eventImagePreviewUrlRef.current);
        eventImagePreviewUrlRef.current = null;
      }
      setEventDialogOpen(false);
    } finally {
      setSavingEvent(false);
    }
  };

  const handleEventDelete = async () => {
    if (!deleteEventId) return;
    await removeEvent({ id: deleteEventId });
    setDeleteEventId(null);
  };

  // Referrals handlers
  const handleReferralStatusChange = async (id: Id<"referrals">, status: string) => {
    await updateReferralStatus({ id, status });
  };

  const handleReferralDelete = async () => {
    if (!deleteReferralId) return;
    await removeReferral({ id: deleteReferralId });
    setDeleteReferralId(null);
  };

  const handleBrochureSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") || (file.type && file.type !== "application/pdf")) {
      toast.error("Please choose a PDF file.");
      return;
    }
    if (file.size > MAX_BROCHURE_BYTES) {
      toast.error("The conference brochure must be 25 MB or smaller.");
      return;
    }

    setUploadingBrochure(true);
    let uploadedStorageId: Id<"_storage"> | null = null;
    try {
      const uploadUrl = await generateBrochureUploadUrl({});
      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": "application/pdf" },
        body: file,
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status})`);
      const result = await response.json() as { storageId: Id<"_storage"> };
      uploadedStorageId = result.storageId;
      await publishBrochure({ storageId: result.storageId, fileName: file.name });
      uploadedStorageId = null;
      toast.success("Conference brochure published.");
    } catch (error) {
      console.error(error);
      if (uploadedStorageId) {
        void discardBrochureUpload({ storageId: uploadedStorageId }).catch(console.error);
      }
      toast.error(error instanceof Error ? error.message : "Brochure upload failed. Please try again.");
    } finally {
      setUploadingBrochure(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-6 py-10 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-semibold text-foreground mb-2 tracking-tight">
            Content Management
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base tracking-wide">
            Manage updates, members, events, referrals, and the conference brochure
          </p>
        </div>

        <Tabs defaultValue="updates">
          <TabsList className="mb-6 h-auto flex-wrap">
            <TabsTrigger value="updates">Updates</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="conference">Conference</TabsTrigger>
            <TabsTrigger value="referrals">Referrals</TabsTrigger>
          </TabsList>

          {/* ==================== UPDATES TAB ==================== */}
          <TabsContent value="updates">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground">
                Updates ({updates?.length ?? "…"})
              </h2>
              <Button onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" />
                New Update
              </Button>
            </div>

            {/* Loading State */}
            {updates === undefined && (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="bg-card border-border/40">
                    <CardContent className="p-5 space-y-3">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Updates List */}
            {updates && (
              <div className="space-y-4">
                {updates.length === 0 && (
                  <Card className="bg-card border-border/40">
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">No updates yet. Create your first one!</p>
                    </CardContent>
                  </Card>
                )}
                {updates.map((update) => (
                  <Card key={update._id} className="bg-card border-border/40">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-foreground mb-1 truncate">
                            {update.title}
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Badge variant="outline" className="text-xs font-medium">
                              {update.category}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{update.author}</span>
                            <span className="text-xs text-muted-foreground">{update.date}</span>
                            {update.vimeoId && (
                              <Badge variant="outline" className="text-xs">Video</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{update.excerpt}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(update)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteId(update._id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ==================== MEMBERS TAB ==================== */}
          <TabsContent value="members">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground">
                Members ({members?.length ?? "…"})
              </h2>
              <Button onClick={openMemberCreate}>
                <Plus className="w-4 h-4 mr-2" />
                New Member
              </Button>
            </div>

            {/* Loading State */}
            {members === undefined && (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Card key={i} className="bg-card border-border/40">
                    <CardContent className="p-4 flex items-center gap-4">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-1/3" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Members List */}
            {members && (
              <div className="space-y-3">
                {members.length === 0 && (
                  <Card className="bg-card border-border/40">
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">No members yet. Add your first one!</p>
                    </CardContent>
                  </Card>
                )}
                {members.map((member) => (
                  <Card key={member._id} className="bg-card border-border/40">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-foreground truncate">
                            {member.name}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate">
                            <span className="font-medium text-foreground/80">{member.company}</span>
                            {member.title && (
                              <>
                                <span className="mx-2">|</span>
                                <span>{member.title}</span>
                              </>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="icon" onClick={() => openMemberEdit(member)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteMemberId(member._id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ==================== REFERRALS TAB ==================== */}
          <TabsContent value="referrals">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground">
                Referrals ({referrals?.length ?? "…"})
              </h2>
            </div>

            {/* Loading State */}
            {referrals === undefined && (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="bg-card border-border/40">
                    <CardContent className="p-5 space-y-3">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Referrals List */}
            {referrals && (
              <div className="space-y-4">
                {referrals.length === 0 && (
                  <Card className="bg-card border-border/40">
                    <CardContent className="p-8 text-center">
                      <UserPlus className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No referrals yet. Members can submit referrals from the Refer a Peer page.</p>
                    </CardContent>
                  </Card>
                )}
                {referrals.map((referral) => (
                  <Card key={referral._id} className="bg-card border-border/40">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-foreground mb-1">
                            {referral.peerName}
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Badge
                              variant="secondary"
                              className={
                                referral.status === "New"
                                  ? "bg-blue-600 text-white hover:bg-blue-600"
                                  : referral.status === "Contacted"
                                    ? "bg-yellow-600 text-white hover:bg-yellow-600"
                                    : referral.status === "Approved"
                                      ? "bg-green-600 text-white hover:bg-green-600"
                                      : "bg-red-600 text-white hover:bg-red-600"
                              }
                            >
                              {referral.status}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(referral.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {referral.peerEmail}
                            {referral.peerPhone && ` · ${referral.peerPhone}`}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground/80">{referral.peerCompany}</span>
                            <span className="mx-2">|</span>
                            <span>{referral.peerTitle}</span>
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Referred by {referral.referrerName} ({referral.referrerEmail})
                          </p>
                          {referral.message && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              "{referral.message}"
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Select
                            value={referral.status}
                            onValueChange={(val) => handleReferralStatusChange(referral._id, val)}
                          >
                            <SelectTrigger className="w-[130px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {["New", "Contacted", "Approved", "Rejected"].map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteReferralId(referral._id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ==================== EVENTS TAB ==================== */}
          <TabsContent value="events">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground">
                Events ({events?.length ?? "…"})
              </h2>
              <Button onClick={openEventCreate}>
                <Plus className="w-4 h-4 mr-2" />
                New Event
              </Button>
            </div>

            {/* Loading State */}
            {events === undefined && (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="bg-card border-border/40">
                    <CardContent className="p-5 space-y-3">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Events List */}
            {events && (
              <div className="space-y-4">
                {events.length === 0 && (
                  <Card className="bg-card border-border/40">
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">No events yet. Create your first one!</p>
                    </CardContent>
                  </Card>
                )}
                {events.map((event) => {
                  const isPast = event.date < today;
                  return (
                    <Card
                      key={event._id}
                      className={`bg-card border-border/40 ${isPast ? "opacity-50" : ""}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-base font-semibold text-foreground truncate">
                                {event.title}
                              </h3>
                              {event.pinned && (
                                <Pin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <Badge variant="outline" className="text-xs font-medium">
                                {event.type}
                              </Badge>
                              <span className="text-xs text-muted-foreground">{event.dateDisplay}</span>
                              {isPast && (
                                <Badge variant="outline" className="text-xs">Past</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {event.time} | {event.location}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Edit ${event.title}`}
                              onClick={() => openEventEdit(event)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Delete ${event.title}`}
                              onClick={() => setDeleteEventId(event._id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ==================== CONFERENCE TAB ==================== */}
          <TabsContent value="conference">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-foreground">Conference brochure</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Upload the current PDF. Publishing a new file immediately replaces the brochure members see.
              </p>
            </div>

            <Card className="bg-card border-border/40">
              <CardContent className="p-6">
                {brochure === undefined ? (
                  <div className="space-y-3">
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <FileText className="mt-0.5 h-6 w-6 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">
                          {brochure?.fileName ?? "No brochure uploaded"}
                        </p>
                        {brochure ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {formatFileSize(brochure.size)} · Updated {new Date(brochure.updatedAt).toLocaleString()}
                          </p>
                        ) : (
                          <p className="mt-1 text-sm text-muted-foreground">
                            Upload a PDF to make the brochure available to members.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {brochure && (
                        <Button variant="outline" asChild>
                          <a href="/conference/brochure" target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="mr-2 h-4 w-4" />
                            Open current PDF
                          </a>
                        </Button>
                      )}
                      <input
                        ref={brochureInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={handleBrochureSelect}
                        aria-label="Choose conference brochure PDF"
                      />
                      <Button
                        onClick={() => brochureInputRef.current?.click()}
                        disabled={uploadingBrochure}
                      >
                        {uploadingBrochure ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="mr-2 h-4 w-4" />
                        )}
                        {brochure ? "Replace PDF" : "Upload PDF"}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <p className="mt-3 text-xs text-muted-foreground">PDF only, up to 25 MB.</p>
          </TabsContent>
        </Tabs>

        {/* ==================== UPDATE DIALOGS ==================== */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden break-words">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Update" : "New Update"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Title</Label>
                <Input
                  ref={updateTitleRef}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Update title"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Author</Label>
                  <Input
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date</Label>
                  {isMobile ? (
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          const dt = new Date(val + "T12:00:00");
                          setForm({
                            ...form,
                            date: dt.toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }),
                          });
                        } else {
                          setForm({ ...form, date: "" });
                        }
                      }}
                    />
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full justify-start text-left font-normal ${!form.date ? "text-muted-foreground" : ""}`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {form.date || "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.date ? new Date(form.date) : undefined}
                          onSelect={(day) =>
                            setForm({
                              ...form,
                              date: day
                                ? day.toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })
                                : "",
                            })
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select
                    value={form.category}
                    onValueChange={(val) => setForm({ ...form, category: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Vimeo Video (optional)</Label>
                  <Input
                    value={form.vimeoId}
                    onChange={(e) =>
                      setForm({ ...form, vimeoId: e.target.value })
                    }
                    placeholder="Paste Vimeo link or ID"
                  />
                  {form.vimeoId && parseVimeoInput(form.vimeoId) && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Video detected: {parseVimeoInput(form.vimeoId)}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <Label>Summary</Label>
                <Textarea
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  placeholder="Brief summary"
                  rows={4}
                />
              </div>

              <div>
                <Label className="mb-1 block">Content</Label>
                <TiptapEditor
                  content={form.content}
                  onChange={(md) => setForm({ ...form, content: md })}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving || !form.title.trim() || !form.date.trim() || !form.excerpt.trim()}>
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : editingId ? "Save Changes" : "Create Update"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Update Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Update</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this update? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ==================== MEMBER DIALOGS ==================== */}
        <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingMemberId ? "Edit Member" : "New Member"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Name</Label>
                <Input
                  value={memberForm.name}
                  onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                  placeholder="Full name"
                />
              </div>
              <div>
                <Label>Company</Label>
                <Input
                  value={memberForm.company}
                  onChange={(e) => setMemberForm({ ...memberForm, company: e.target.value })}
                  placeholder="Company name"
                />
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={memberForm.title}
                  onChange={(e) => setMemberForm({ ...memberForm, title: e.target.value })}
                  placeholder="Job title"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setMemberDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleMemberSave} disabled={savingMember || !memberForm.name.trim()}>
                  {savingMember ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : editingMemberId ? "Save Changes" : "Create Member"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Member Confirmation */}
        <AlertDialog open={!!deleteMemberId} onOpenChange={(open) => !open && setDeleteMemberId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Member</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this member? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleMemberDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ==================== EVENT DIALOGS ==================== */}
        <Dialog open={eventDialogOpen} onOpenChange={handleEventDialogOpenChange}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEventId ? "Edit Event" : "New Event"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Title *</Label>
                <Input
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  placeholder="e.g. ETP Member Call"
                  className={eventErrors.title ? "border-destructive" : ""}
                />
                {eventErrors.title && <p className="text-xs text-destructive mt-1">{eventErrors.title}</p>}
              </div>

              {/* Event photo */}
              <div>
                <Label>Photo</Label>
                <input
                  ref={eventImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleEventImageSelect}
                />
                {eventForm.imageUrl ? (
                  <div className="relative mt-1 overflow-hidden rounded-md border border-input">
                    <img
                      src={eventForm.imageUrl}
                      alt="Event"
                      className="h-40 w-full object-cover"
                    />
                    <div className="absolute right-2 top-2 flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-8"
                        onClick={() => eventImageInputRef.current?.click()}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : "Replace"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-8 w-8 p-0"
                        onClick={handleEventImageRemove}
                        disabled={uploadingImage}
                        aria-label="Remove photo"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => eventImageInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="mt-1 flex h-28 w-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-input text-muted-foreground transition-colors hover:bg-accent/40 disabled:opacity-60"
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-xs">Uploading…</span>
                      </>
                    ) : (
                      <>
                        <ImagePlus className="h-5 w-5" />
                        <span className="text-xs">Upload a photo (optional)</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 mb-1.5">
                  <Label>Date *</Label>
                  <ToggleGroup
                    type="single"
                    value={eventForm.dateTbd ? "month" : "day"}
                    onValueChange={(val) => {
                      if (!val) return; // one option is always selected
                      setEventForm({
                        ...eventForm,
                        dateTbd: val === "month",
                        endDate: "",
                        dateDisplay: "",
                      });
                    }}
                    className="gap-0 rounded-lg border border-input bg-muted/40 p-0.5"
                  >
                    <ToggleGroupItem
                      value="day"
                      size="sm"
                      className="h-7 rounded-md px-3 text-xs font-medium text-muted-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                    >
                      Specific day
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="month"
                      size="sm"
                      className="h-7 rounded-md px-3 text-xs font-medium text-muted-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                    >
                      Month only
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                {eventForm.dateTbd ? (
                  <Input
                    type="month"
                    value={eventForm.date ? eventForm.date.slice(0, 7) : ""}
                    onChange={(e) =>
                      setEventForm({
                        ...eventForm,
                        date: e.target.value ? `${e.target.value}-01` : "",
                        dateDisplay: "",
                      })
                    }
                    className={eventErrors.date ? "border-destructive" : ""}
                  />
                ) : (
                  <Input
                    type="date"
                    value={eventForm.date}
                    onChange={(e) => setEventForm({ ...eventForm, date: e.target.value, dateDisplay: "" })}
                    className={eventErrors.date ? "border-destructive" : ""}
                  />
                )}
                {eventForm.dateTbd && (
                  <div className="mt-1.5 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      For events with no set day yet — members see just the month, e.g. “August 2026”.
                    </p>
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <Checkbox
                        checked={!eventForm.hideTbdBadge}
                        onCheckedChange={(checked) =>
                          setEventForm({ ...eventForm, hideTbdBadge: checked !== true })
                        }
                        className="h-4 w-4"
                      />
                      <span className="text-muted-foreground">Show “Date TBD” badge to members</span>
                    </label>
                  </div>
                )}
                {eventErrors.date && <p className="text-xs text-destructive mt-1">{eventErrors.date}</p>}
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={eventForm.location}
                  onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                  placeholder="e.g. Virtual via Zoom"
                />
              </div>
              {!eventForm.dateTbd && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={eventForm.startTime}
                      onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={eventForm.endTime}
                      onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Timezone</Label>
                    <Select
                      value={eventForm.timezone}
                      onValueChange={(val) => setEventForm({ ...eventForm, timezone: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timezones.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select
                    value={eventForm.type}
                    onValueChange={(val) => setEventForm({ ...eventForm, type: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={eventForm.status}
                    onValueChange={(val) => setEventForm({ ...eventForm, status: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {eventStatuses.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Zoom details */}
              <div>
                <Label>Zoom Link</Label>
                <Input
                  value={eventForm.zoomLink}
                  onChange={(e) => setEventForm({ ...eventForm, zoomLink: e.target.value })}
                  placeholder="https://zoom.us/..."
                />
              </div>
              {eventForm.zoomLink && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Meeting ID</Label>
                    <Input
                      value={eventForm.meetingId}
                      onChange={(e) => setEventForm({ ...eventForm, meetingId: e.target.value })}
                      placeholder="829 8862 7410"
                    />
                  </div>
                  <div>
                    <Label>Passcode</Label>
                    <Input
                      value={eventForm.passcode}
                      onChange={(e) => setEventForm({ ...eventForm, passcode: e.target.value })}
                      placeholder="089556"
                    />
                  </div>
                </div>
              )}

              {/* Preview of auto-generated display */}
              {eventForm.date && (
                <div className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground/80">Preview: </span>
                  {eventForm.dateDisplay ||
                    (eventForm.dateTbd
                      ? generateMonthYearDisplay(eventForm.date)
                      : generateDateDisplay(eventForm.date, eventForm.endDate))}
                  {!eventForm.dateTbd && (
                    <>
                      {" — "}
                      {eventForm.time || generateTimeDisplay(eventForm.startTime, eventForm.endTime, eventForm.timezone)}
                    </>
                  )}
                </div>
              )}

              {/* Advanced options */}
              <details className="group">
                <summary className="flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors select-none">
                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                  More options
                </summary>
                <div className="space-y-4 pt-3">
                  {!eventForm.dateTbd && (
                    <div>
                      <Label>End Date (multi-day events only)</Label>
                      <Input
                        type="date"
                        value={eventForm.endDate}
                        onChange={(e) => setEventForm({ ...eventForm, endDate: e.target.value })}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Display Date override</Label>
                      <Input
                        value={eventForm.dateDisplay}
                        onChange={(e) => setEventForm({ ...eventForm, dateDisplay: e.target.value })}
                        placeholder="Auto-generated if empty"
                      />
                    </div>
                    <div>
                      <Label>Display Time override</Label>
                      <Input
                        value={eventForm.time}
                        onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                        placeholder="Auto-generated if empty"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tier Required</Label>
                      <Input
                        value={eventForm.tierRequired}
                        onChange={(e) => setEventForm({ ...eventForm, tierRequired: e.target.value })}
                        placeholder="e.g. 50K+ Members"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-2 block">&nbsp;</Label>
                      <label htmlFor="pinned" className="flex items-center gap-3 h-10 px-3 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors">
                        <Checkbox
                          id="pinned"
                          checked={eventForm.pinned}
                          onCheckedChange={(checked) =>
                            setEventForm({ ...eventForm, pinned: checked === true })
                          }
                          className="h-5 w-5"
                        />
                        <span className="text-sm">Pinned (appears first)</span>
                      </label>
                    </div>
                  </div>
                </div>
              </details>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => handleEventDialogOpenChange(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEventSave} disabled={savingEvent || uploadingImage || !eventForm.title.trim()}>
                  {savingEvent ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : editingEventId ? "Save Changes" : "Create Event"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Event Confirmation */}
        <AlertDialog open={!!deleteEventId} onOpenChange={(open) => !open && setDeleteEventId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Event</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this event? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleEventDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Referral Confirmation */}
        <AlertDialog open={!!deleteReferralId} onOpenChange={(open) => !open && setDeleteReferralId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Referral</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this referral? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleReferralDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default Admin;
